import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PoweredByFooter from "../components/PoweredByFooter";
import {
  ArrowLeft, ChevronDown, ChevronRight, HelpCircle,
  LogIn, Map, Users, Shield, Clock, Package, FileSpreadsheet,
  Briefcase, QrCode, Mail, Settings, BarChart3, MessageSquare,
  UserPlus, Search, Download, Upload, Layers, Eye, Trash2
} from "lucide-react";

// Guide-sektioner defineret som data
const GUIDE_SECTIONS = {
  admin: [
    {
      id: "login",
      title: "Log ind som Administrator",
      icon: LogIn,
      steps: [
        "Gå til login-siden og klik på \"Admin\" fanen.",
        "Indtast din email og adgangskode.",
        "Klik \"Log ind\". Du kommer nu til Event-vælgeren."
      ]
    },
    {
      id: "events",
      title: "Opret og administrer events",
      icon: Layers,
      steps: [
        "På Event-vælgeren klik \"Opret nyt event\" i øverste højre hjørne.",
        "Udfyld event-navn, datoer og beskrivelse.",
        "Du kan uploade et event-logo ved at klikke på logo-ikonet på event-kortet.",
        "Klik \"Åbn Event\" for at gå ind i et event.",
        "For at arkivere et event, klik på arkiv-ikonet på event-kortet."
      ]
    },
    {
      id: "qr-links",
      title: "QR-koder, Links og Invitationer",
      icon: QrCode,
      steps: [
        "På hvert event-kort finder du tre knapper: QR-kode, Kopier link og Inviter.",
        "\"QR-kode\" downloader en QR-kode som billede — brug den i trykt materiale eller på skærme.",
        "\"Kopier link\" kopierer et direkte link til eventet — del det via SMS, chat eller sociale medier.",
        "\"Inviter\" sender en professionel e-mail invitation med event-info, link og QR-kode til en ny arrangør."
      ]
    },
    {
      id: "stamdata",
      title: "Stamdata — Opsætning af dit event",
      icon: FileSpreadsheet,
      steps: [
        "Gå til \"Stamdata\" under Administration på dashboardet.",
        "Her finder du 8 faner: Filer, Vagter, Funktioner, Teams, Frivillige, Adgangsstyring, Materiel og Ressourcer."
      ],
      subsections: [
        {
          title: "Filer — Upload Excel",
          steps: [
            "Upload separate Excel-filer for frivillige og teams.",
            "Systemet viser en forhåndsvisning, hvor du vælger hvilke kolonner der skal importeres.",
            "Du kan altid erstatte en fil med en ny version."
          ]
        },
        {
          title: "Vagter — Opret vagtplaner",
          steps: [
            "Klik \"Tilføj vagt\" og udfyld dato, start- og sluttid.",
            "Vagter grupperes automatisk efter dato.",
            "Du kan redigere eller slette vagter med ikonerne til højre."
          ]
        },
        {
          title: "Funktioner — Overordnede roller",
          steps: [
            "Opret funktioner som \"Sweeper\", \"Chauffør\", \"Guide\" osv.",
            "Angiv om funktionen har flere områder eller arbejdszoner.",
            "Teams kan tilknyttes en funktion."
          ]
        },
        {
          title: "Teams — Opret og konfigurer",
          steps: [
            "Klik \"Opret nyt team\" og giv det et navn.",
            "Du kan basere et nyt team på en eksisterende skabelon.",
            "Klik på et team for at konfigurere: rettigheder, vagter, område, beskeder og kapacitet.",
            "Under \"Rettigheder\" kan du styre hvad teamet kan se og gøre (zoner, ressourceøer, spande osv.).",
            "Klik \"Gem som skabelon\" for at genbruge indstillingerne til nye teams."
          ]
        },
        {
          title: "Frivillige — Administrer deltagere",
          steps: [
            "Se alle frivillige i en oversigtstabel med søgefunktion.",
            "Tilføj frivillige manuelt med \"Tilføj frivillig\" knappen.",
            "Eksporter alle frivillige som CSV med \"Eksporter CSV\" knappen.",
            "Når du uploader en ny Excel-fil, markeres frivillige der ikke længere er i filen — du kan beholde eller slette dem."
          ]
        },
        {
          title: "Adgangsstyring — Roller og rettigheder",
          steps: [
            "Her styrer du hvad hver brugerrolle kan se og gøre i systemet.",
            "Tabellen har 5 roller: Admin, Admin Assistent, Arrangør, Leder og Frivillig.",
            "Admin har altid fuld adgang (kan ikke ændres).",
            "Sæt flueben for at give en rolle adgang til et modul eller en handling.",
            "Nedenfor kan du oprette Admin Assistenter og Arrangører og tildele dem til specifikke events."
          ]
        },
        {
          title: "Ressourcer — Fraktioner og containere",
          steps: [
            "Vælg mellem Ressourceøer, Ressourcespande og Maxicontainere.",
            "Tilføj fraktionstyper (f.eks. Rest, Pap, Plast) med farver.",
            "Tilføj containerstørrelser (f.eks. 240L, 660L) til beregninger."
          ]
        }
      ]
    },
    {
      id: "kort",
      title: "Kortet — Ressourcehåndtering",
      icon: Map,
      steps: [
        "Gå til \"Kort & Ressourcer\" på dashboardet.",
        "Upload et kort-billede som baggrund (f.eks. et festivalområde-kort).",
        "Placer ressourcepunkter ved at klikke på kortet.",
        "Opret zoner ved at tegne områder.",
        "Hvert punkt kan have fraktioner, containere og fyldte-registreringer."
      ]
    },
    {
      id: "brugere",
      title: "Opret nye brugere",
      icon: UserPlus,
      steps: [
        "Gå til Stamdata > Adgangsstyring.",
        "Klik \"Tilføj\" under Admin Assistenter eller Arrangører.",
        "Udfyld navn, email og adgangskode.",
        "Tildel brugeren til et eller flere events.",
        "Admin Assistenter har næsten fuld adgang. Arrangører har begrænset adgang (ingen frivillige eller adgangsstyring)."
      ]
    },
    {
      id: "platform",
      title: "Platform-indstillinger",
      icon: Settings,
      steps: [
        "Klik på tandhjulet i Event-vælgeren for at åbne Platform-indstillinger.",
        "Upload dit host-logo (f.eks. OurChoice-logoet).",
        "Logoet vises på login-siden og i \"Powered by\"-footeren."
      ]
    },
    {
      id: "analyse",
      title: "Analyse og rapporter",
      icon: BarChart3,
      steps: [
        "Gå til \"Analyse\" på dashboardet.",
        "Se statistik over affald, ressourcer, frivillige og zoner.",
        "Eksporter data som Excel eller CSV til videre analyse."
      ]
    }
  ],
  arrangør: [
    {
      id: "arr-login",
      title: "Log ind som Arrangør",
      icon: LogIn,
      steps: [
        "Gå til login-siden og klik på \"Admin\" fanen.",
        "Indtast den email og adgangskode du har fået fra administratoren.",
        "Du kommer til Event-vælgeren, hvor du ser de events du er tildelt."
      ]
    },
    {
      id: "arr-dashboard",
      title: "Dashboardet — Dit overblik",
      icon: Layers,
      steps: [
        "Klik \"Åbn Event\" for at gå ind i dit event.",
        "Dashboardet viser de moduler du har adgang til."
      ]
    },
    {
      id: "arr-stamdata",
      title: "Stamdata — Tilføj data",
      icon: FileSpreadsheet,
      steps: [
        "Gå til \"Stamdata\" for at arbejde med dit events grunddata.",
        "Du kan se: Filer, Vagter, Funktioner, Teams, Materiel og Ressourcer.",
        "Tilføj fraktioner, materialer, vagter og teams efter behov."
      ]
    },
    {
      id: "arr-kort",
      title: "Kortet — Placer ressourcer",
      icon: Map,
      steps: [
        "Gå til \"Kort & Ressourcer\" for at se festivalkortet.",
        "Du kan placere ressourcepunkter på kortet."
      ]
    },
    {
      id: "arr-beskeder",
      title: "Beskeder",
      icon: MessageSquare,
      steps: [
        "Brug beskedsystemet til at kommunikere med teams.",
        "Du kan sende og modtage beskeder inden for dit event."
      ]
    },
    {
      id: "arr-analyse",
      title: "Analyse og rapporter",
      icon: BarChart3,
      steps: [
        "Gå til \"Analyse\" på dashboardet.",
        "Se statistik over affald, ressourcer, frivillige og zoner.",
        "Eksporter data som Excel eller CSV til videre analyse."
      ]
    }
  ],
  frivillig: [
    {
      id: "vol-login",
      title: "Log ind som Frivillig",
      icon: LogIn,
      steps: [
        "Gå til login-siden og vælg \"Frivillig\" fanen.",
        "Vælg dit event i dropdown-menuen.",
        "Indtast din email-adresse.",
        "Vælg din rolle, vagt og eventuelt område.",
        "Klik \"Log ind\"."
      ]
    },
    {
      id: "vol-dashboard",
      title: "Dit Dashboard",
      icon: Layers,
      steps: [
        "Se dit kort med de punkter der er relevante for din rolle.",
        "Se din vagtplan og hvornår du skal møde.",
        "Læs beskeder fra din teamleder."
      ]
    },
    {
      id: "vol-kort",
      title: "Kortet",
      icon: Map,
      steps: [
        "Kortet viser de ressourcepunkter og zoner du har adgang til.",
        "Klik på et punkt for at se detaljer.",
        "Registrer affald og fyldte containere direkte fra kortet."
      ]
    }
  ]
};

function GuideSection({ section, isOpen, onToggle }) {
  const Icon = section.icon;
  return (
    <div className="border border-border rounded-lg overflow-hidden mb-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors text-left"
        data-testid={`guide-section-${section.id}`}
      >
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <span className="font-semibold flex-1">{section.title}</span>
        {isOpen ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-border/50">
          <ol className="mt-3 space-y-2">
            {section.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">{i + 1}</span>
                <span className="text-muted-foreground pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
          {section.subsections && (
            <div className="mt-4 space-y-3">
              {section.subsections.map((sub, i) => (
                <div key={i} className="pl-4 border-l-2 border-primary/30">
                  <h4 className="font-medium text-sm mb-2">{sub.title}</h4>
                  <ol className="space-y-1.5">
                    {sub.steps.map((step, j) => (
                      <li key={j} className="flex gap-2 text-sm">
                        <span className="text-primary/60 flex-shrink-0">•</span>
                        <span className="text-muted-foreground">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Brugsvejledning({ user }) {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState(
    user?.role === 'arrangør' ? 'arrangør' : user?.role === 'admin' || user?.role === 'admin_assistant' ? 'admin' : 'frivillig'
  );
  const [openSections, setOpenSections] = useState(new Set());

  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = GUIDE_SECTIONS[activeRole].map(s => s.id);
    setOpenSections(new Set(allIds));
  };

  const collapseAll = () => setOpenSections(new Set());

  const roles = [
    { key: 'admin', label: 'Administrator', icon: Shield, color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { key: 'arrangør', label: 'Arrangør', icon: Briefcase, color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { key: 'frivillig', label: 'Frivillig', icon: Users, color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="guide-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <HelpCircle className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">Brugsvejledning</h1>
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Trin-for-trin guide til Resovia</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Rolle-vælger */}
        <div className="mb-8">
          <p className="text-sm text-muted-foreground mb-3">Vælg din rolle for at se den relevante vejledning:</p>
          <div className="flex gap-3 flex-wrap">
            {roles.map(r => {
              const Icon = r.icon;
              return (
                <button
                  key={r.key}
                  onClick={() => { setActiveRole(r.key); setOpenSections(new Set()); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    activeRole === r.key
                      ? `${r.color} border-current`
                      : 'bg-muted/20 border-border text-muted-foreground hover:border-primary/30'
                  }`}
                  data-testid={`guide-role-${r.key}`}
                >
                  <Icon className="h-4 w-4" />
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Kontrolknapper */}
        <div className="flex gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={expandAll}>Åbn alle</Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>Luk alle</Button>
        </div>

        {/* Guide-sektioner */}
        <div>
          {(GUIDE_SECTIONS[activeRole] || []).map(section => (
            <GuideSection
              key={section.id}
              section={section}
              isOpen={openSections.has(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>

        {/* Hurtig-reference */}
        <div className="mt-10 p-6 bg-card border border-border rounded-lg">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Hurtig Reference
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Genveje</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Klik på logoet i top-baren for at gå til dashboardet</li>
                <li>• Brug pil-tilbage knappen for at gå et niveau op</li>
                <li>• Klik \"Skift event\" for at vælge et andet event</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Eksport af data</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• CSV-filer downloades til din browsers standard download-mappe</li>
                <li>• I Chrome: Tryk Ctrl+J for at se downloads</li>
                <li>• Excel-eksporter fra Analyse findes samme sted</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <PoweredByFooter />
    </div>
  );
}
